import { PortfolioDatesData, PortfolioPhaseDates } from "../../../../models";
import { calculateApproximateYearsAndMonthsBetween, convertDateToStringDDMMYYYY } from "../../../../utils";
import {
  PIPELINE_MILESTONE_COLOR_MAP,
  PIPELINE_MILESTONE_COLORS,
  PIPELINE_PHASE_COLOR_PAIRS,
  PortfolioRoadmapRowLabelData,
  ViewMode,
} from "../models";

type NormalizedPhase = {
  key: string;
  label: string;
  start: Date;
  end: Date;
  status?: string | null;
};

const toDate = (value?: Date | string | null): Date | undefined => {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
};

const toTitleCase = (value: string): string =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const normalizePhase = (key: string, phase: PortfolioPhaseDates): NormalizedPhase | undefined => {
  const start = toDate(phase.start);
  const end = toDate(phase.end ?? phase.start);
  if (!start || !end) return undefined;
  return {
    key,
    label: toTitleCase(key),
    start,
    end,
    status: phase.status,
  };
};

const getPhases = (item: PortfolioDatesData): NormalizedPhase[] =>
  Object.entries(item.dates)
    .map(([key, phase]) => normalizePhase(key, phase))
    .filter((p): p is NormalizedPhase => !!p)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

export const getFormatFromViewMode = (viewMode: ViewMode): string => {
  switch (viewMode) {
    case ViewMode.Months1:
    case ViewMode.Months3:
      return "MMM\ndd";
    case ViewMode.Months6:
      return "yyyy\nMMM";
    case ViewMode.Years1:
    case ViewMode.Years2:
    default:
      return "yyyy\nQQ";
  }
};

export const getPortfolioRoadmapRowLabelData = (data: PortfolioDatesData[]): PortfolioRoadmapRowLabelData => {
  return data.map((item) => {
    return {
      displayName: item.displayName,
      subtitle: item.subtitle,
      uuid: item.uuid,
      additionalDates: item.hasOverlappingDates,
    };
  });
};

const getPhaseColors = (index: number): { stroke: string; fill: string; milestoneFill: string } => {
  const [stroke, fill] = PIPELINE_PHASE_COLOR_PAIRS[index % PIPELINE_PHASE_COLOR_PAIRS.length];
  const milestoneFill = PIPELINE_MILESTONE_COLORS[index % PIPELINE_MILESTONE_COLORS.length];
  return { stroke, fill, milestoneFill };
};

const getCustomTooltipContent = (
  label: string,
  project: string,
  startDate: Date,
  endDate: Date,
  status?: string | null,
  isMilestone = false
): string => {
  const duration =
    startDate.getTime() !== endDate.getTime()
      ? calculateApproximateYearsAndMonthsBetween(startDate, endDate)
      : undefined;

  return `<div class="CustomTooltip">
            <div class="TooltipHeader">
              <p class="body2"><b>${project}</b></p>
              <div class="CloseTooltipButton" onclick="window.dispatchEvent(new Event('closeTooltip'));"><p class="body1"><b>x</b></p></div>
            </div>
            <div class="TooltipContent">
              <p class="body2"><b>${label}</b></p>
              ${status ? `<p class="body2"><b>Status:</b> ${status}</p>` : ""}
              ${
                !duration
                  ? `<p class="body2">${isMilestone ? "<b>Deadline:</b> " : ""}${convertDateToStringDDMMYYYY(startDate)}</p>`
                  : ""
              }
              ${
                duration
                  ? `<p class="body2"><b>Start/end:</b> ${convertDateToStringDDMMYYYY(startDate)} - ${convertDateToStringDDMMYYYY(endDate)}</p>`
                  : ""
              }
              ${
                duration && (duration.years > 0 || duration.months > 0)
                  ? `<p class="body2"><b>Duration:</b> ${duration.years > 0 ? `${duration.years} year${duration.years > 1 ? "s" : ""} ` : ""}${duration.months > 0 ? `${duration.months} month${duration.months > 1 ? "s" : ""}` : ""}</p>`
                  : ""
              }
            </div>
          </div>`;
};

const createRow = (
  displayName: string,
  label: string,
  tooltip: string,
  style: string,
  startDate: Date,
  endDate: Date
): (string | Date)[] => [displayName, label, tooltip, style, startDate, endDate];

export const getPortfolioRoadmapTimelineData = (data: PortfolioDatesData[], showMilestones: boolean): unknown[] => {
  const format: unknown[] = [
    [
      { type: "string", id: "Project" },
      { type: "string", id: "Phase" },
      { type: "string", role: "tooltip" },
      { type: "string", role: "style" },
      { type: "date", id: "Start" },
      { type: "date", id: "End" },
    ],
  ];

  const rows = data.flatMap((item) => {
    const phases = getPhases(item);
    const phaseIndexByKey = new Map(phases.map((phase, index) => [phase.key, index]));

    const phaseRows = phases.map((phase, index) => {
      const colors = getPhaseColors(index);
      const style = `stroke-color: ${colors.stroke}; fill-color: ${colors.fill}; stroke-width: 1`;
      const tooltip = getCustomTooltipContent(phase.label, item.displayName, phase.start, phase.end, phase.status);
      return createRow(item.displayName, phase.label, tooltip, style, phase.start, phase.end);
    });

    if (!showMilestones) return phaseRows;

    const milestoneRows = (item.milestones ?? [])
      .map((milestone) => {
        const milestoneDate = toDate(milestone.date);
        if (!milestoneDate) return undefined;

        const phaseIndex = milestone.phaseKey ? (phaseIndexByKey.get(milestone.phaseKey) ?? 0) : 0;
        const colors = getPhaseColors(phaseIndex);
        const { milestoneFill } = colors;
        const milestoneStroke = PIPELINE_MILESTONE_COLOR_MAP[milestoneFill] ?? milestoneFill;
        const style = `stroke-color: ${milestoneStroke}; fill-color: ${milestoneFill}; stroke-width: 1`;
        const tooltip = getCustomTooltipContent(
          milestone.label,
          item.displayName,
          milestoneDate,
          milestoneDate,
          undefined,
          true
        );
        return createRow(item.displayName, milestone.label, tooltip, style, milestoneDate, milestoneDate);
      })
      .filter((row): row is (string | Date)[] => !!row);

    return [...phaseRows, ...milestoneRows];
  });

  format.push(...rows);

  return format;
};

const getAllTimelineDates = (data: PortfolioDatesData[], showMilestones: boolean): Date[] => {
  const phaseDates = data.flatMap((item) =>
    Object.values(item.dates)
      .flatMap((phase) => [toDate(phase.start), toDate(phase.end ?? phase.start)])
      .filter((d): d is Date => !!d)
  );

  if (!showMilestones) return phaseDates;

  const milestoneDates = data
    .flatMap((item) => (item.milestones ?? []).map((m) => toDate(m.date)))
    .filter((d): d is Date => !!d);

  return [...phaseDates, ...milestoneDates];
};

export const calculateViewMode = (
  viewMode: ViewMode,
  data: PortfolioDatesData[],
  showMilestones: boolean,
  minWidth = 0
): string => {
  const dates = getAllTimelineDates(data, showMilestones);
  if (dates.length === 0) return `${minWidth}px`;

  const startDate = new Date(Math.min(...dates.map((d) => d.getTime())));
  const endDate = new Date(Math.max(...dates.map((d) => d.getTime())));

  const dateRangeInDays = Math.max(1, (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

  const pixelsPerDayMap: Record<ViewMode, number> = {
    [ViewMode.Months1]: 35,
    [ViewMode.Months3]: 13,
    [ViewMode.Months6]: 6,
    [ViewMode.Years1]: 3,
    [ViewMode.Years2]: 1.5,
  };

  const width = Math.max(dateRangeInDays * (pixelsPerDayMap[viewMode] ?? 2), minWidth);

  return `${width}px`;
};
